/**
 * History Rail — preview on select (HR-2).
 *
 * Selecting a version shows it on the REAL canvas, read-only. This module owns
 * the fetch and the handoff; the shell owns the render.
 *
 * It does NOT build a second render path. AR-5 already shipped one:
 *
 *   getProjectVersionBundle(id, v) → Blob            templates/auth-api.js
 *   readProjectBundle(blob)        → {state, assetSource}   core/save-load.js
 *   'preview:detached-requested'   → shell.js requestAgentReviewRender()
 *                                     · canonicalises + validates the state
 *                                     · flushes the active contenteditable edit
 *                                     · holds WRITE_BLOCKER.DETACHED_PREVIEW
 *                                     · forces a double-buffered render,
 *                                       token-guarded against stale settles
 *   renderToIframe(iframe, state, {assetSource})     renderer/iframe-renderer.js
 *
 * `assetSource` is the load-bearing half: it serves the bundle's OWN images, so
 * a page of an old version renders with the pictures it was saved with rather
 * than whatever the live cache happens to still hold.
 *
 * Contract §3: selection previews, and ONLY the review bar's button commits.
 * Nothing in this file writes to the project.
 */

import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { readProjectBundle } from '../../core/save-load.js';
import { getProjectVersionBundle } from '../../templates/auth-api.js';
import { TemplateApiError } from '../../templates/template-api.js';
import { reportError } from '../../core/report-error.js';

/**
 * Transport and bus are INJECTABLE, defaulting to the real ones — the same
 * shape `version-model.js` uses. The interesting behaviour here is what happens
 * when two fetches race or one fails, and that is only testable if the test can
 * make them race and fail.
 *
 * @param {object} opts
 * @param {string} opts.projectId
 * @param {(state: {status: string, version: number|null, error?: string}) => void} opts.onChange
 * @param {object} [opts.deps]
 */
export function createPreviewController({ projectId, onChange, deps = {} }) {
  const bus = deps.eventBus || eventBus;
  const liveStore = deps.store || store;
  const fetchBundle = deps.getProjectVersionBundle || getProjectVersionBundle;
  const readBundle = deps.readProjectBundle || readProjectBundle;
  const ApiError = deps.TemplateApiError || TemplateApiError;
  const report = deps.reportError || reportError;

  /**
   * Monotonic. Every request carries one, and a result whose token is no longer
   * current is DROPPED rather than rendered. Without this, clicking v19 then v14
   * races two bundle fetches and whichever server response lands last wins —
   * which is not necessarily the row the user is looking at.
   */
  let token = 0;
  let current = null;
  let destroyed = false;

  const tokenFor = (n) => `history-${projectId}-${n}`;

  function emit(status, extra = {}) {
    if (destroyed) return;
    onChange?.({ status, version: current, ...extra });
  }

  return {
    get version() {
      return current;
    },

    /** @param {number|null} version — null returns the canvas to live state. */
    async select(version) {
      const mine = ++token;
      if (version === null || version === undefined) {
        current = null;
        // NO token on a close. `exitAgentReview` treats a token that does not
        // match the live session as STALE and ignores the request, so sending
        // this controller's newest token would leave the canvas detached
        // forever. Omitting it means "close whatever is showing", which is
        // exactly what returning to Now means.
        bus.emit('preview:detached-close-requested', { reason: 'browsing' });
        emit('live');
        return;
      }

      current = Number(version);
      emit('loading');

      let bundle;
      try {
        bundle = await fetchBundle(projectId, current);
      } catch (err) {
        if (mine !== token) return; // superseded mid-fetch — not ours to report
        report('Version preview bundle', err, { user: 'silent', id: 'history-rail-preview' });
        emit('error', {
          error: err instanceof ApiError ? err.message : 'Could not load that version'
        });
        return;
      }
      if (mine !== token) return;

      let read;
      try {
        read = await readBundle(bundle);
      } catch (err) {
        if (mine !== token) return;
        report('Version preview bundle', err, { user: 'silent', id: 'history-rail-preview' });
        emit('error', { error: 'That version could not be opened' });
        return;
      }
      if (mine !== token) return;

      // The shell validates project identity and page presence, holds the write
      // blocker and renders. A superseding request while it works is handled
      // there too — `releaseAgentReview('superseded', …)` hands the canvas over
      // without flashing back through live state.
      bus.emit('preview:detached-requested', {
        owner: 'history',
        detachedState: read.state,
        assetSource: read.assetSource,
        // Stay on the page the user is looking at when that page exists in the
        // target version; the shell resolves and REPORTS the fallback when it
        // does not, rather than treating an older version as an error.
        pageId: liveStore.getState()?.activePage || null,
        previewToken: tokenFor(mine)
      });
      emit('previewing');
    },

    /** Return the canvas to live state without changing the rail's selection. */
    release(reason = 'close') {
      // Bumping the token first invalidates any fetch still in flight, so a
      // bundle that lands after this cannot re-detach the canvas. The close
      // itself carries no token — see select(null).
      token += 1;
      current = null;
      bus.emit('preview:detached-close-requested', { reason });
    },

    destroy() {
      destroyed = true;
      this.release('rail-closed');
    }
  };
}
